import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { CampaignBreadcrumbs } from "@/components/CampaignBreadcrumbs";
import { Button } from "@/components/ui/button";
import { EditableField } from "@/components/character-sheet/EditableField";
import { ParchmentSheet } from "@/components/character-sheet/ParchmentSheet";
import { CompanionStatBlock } from "@/components/CompanionStatBlock";
import { abilityMod, formatMod, proficiencyBonus } from "@/components/character-sheet/diceUtils";
import { ArrowLeft, Heart, Shield, User as UserIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SHEET = {
  abilities: { STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 },
  saving_throws: [],
  skill_proficiencies: [],
  skill_expertise: [],
  current_hp: null, max_hp: null, temp_hp: 0,
  death_saves: { successes: 0, failures: 0 },
  inspiration: false,
  spells: { slots: {}, prepared: [] },
  inventory: { equipment: [], magical_items: [], attuned: [], currency: { cp:0, sp:0, ep:0, gp:0, pp:0 } },
  bio: {},
  features: { racial: [], class: [], feats: [], passive: [] },
};

const CharacterSheetPage = () => {
  const { campaignId, charId } = useParams();
  const navigate = useNavigate();
  const [character, setCharacter] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const saveTimer = useRef(null);

  // Load
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
      axios.get(`${API}/characters/${charId}`, { withCredentials: true }),
    ]).then(([c, ch]) => {
      if (!mounted) return;
      setCampaign(c.data);
      // Ensure sheet has expected shape
      const merged = { ...DEFAULT_SHEET, ...(ch.data.character_sheet || {}) };
      setCharacter({ ...ch.data, character_sheet: merged });
    }).catch(() => { toast.error("Failed to load character"); navigate(`/campaign/${campaignId}/characters`); })
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [campaignId, charId, navigate]);

  // Debounced autosave
  useEffect(() => {
    if (!dirty || !character) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/characters/${charId}`, {
          campaign_id: campaignId,
          name: character.name,
          description: character.description || "",
          character_type: character.character_type || "pc",
          race: character.race || "",
          character_class: character.character_class || "",
          level: parseInt(character.level, 10) || 1,
          location_id: character.location_id || "",
          armor_class: character.armor_class || "",
          hit_points: character.hit_points || "",
          speed: character.speed || "",
          initiative: character.initiative || "",
          player_name: character.player_name || "",
          character_sheet: character.character_sheet || {},
        }, { withCredentials: true });
        setDirty(false);
      } catch { toast.error("Save failed"); }
    }, 700);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [dirty, character, campaignId, charId]);

  const updateSheet = useCallback((patch) => {
    setCharacter(prev => prev ? { ...prev, character_sheet: { ...prev.character_sheet, ...patch } } : prev);
    setDirty(true);
  }, []);
  const updateCharacter = useCallback((patch) => {
    setCharacter(prev => prev ? { ...prev, ...patch } : prev);
    setDirty(true);
  }, []);

  if (loading || !character) {
    return (
      <Layout campaignName={campaign?.name} hideBreadcrumbs>
        <CampaignBreadcrumbs campaignName={campaign?.name} extraCrumbs={[{ label: "Character Sheet" }]} />
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      </Layout>
    );
  }

  const sheet = character.character_sheet || DEFAULT_SHEET;
  const portraitUrl = character.image_path?.startsWith("http") ? character.image_path
    : character.image_path ? `${process.env.REACT_APP_BACKEND_URL}${character.image_path}`
    : null;
  const dexMod = abilityMod(sheet.abilities?.DEX ?? 10);
  const profB = proficiencyBonus(character.level || 1);

  const type = character.character_type || "pc";
  const isCompanion = type === "companion" || type === "summon";
  const isNPC = type === "npc";

  return (
    <Layout campaignName={campaign?.name}>
      <CampaignBreadcrumbs
        campaignName={campaign?.name}
        extraCrumbs={[
          { label: "Characters", to: `/campaign/${campaignId}/characters` },
          { label: character.name }
        ]}
      />

      <div data-testid="character-sheet-page" className="space-y-4">
        {/* HEADER BANNER (sticky) */}
        <div className="sticky top-0 z-20 bg-card/95 backdrop-blur-md border border-border rounded-sm p-4 flex items-center gap-4" data-testid="char-sheet-header">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/campaign/${campaignId}/characters`)} title="Back to characters" data-testid="char-sheet-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          {/* Portrait */}
          <div className="w-16 h-16 rounded-sm bg-muted/20 border border-border overflow-hidden flex items-center justify-center flex-shrink-0">
            {portraitUrl ? (
              <img src={portraitUrl} alt={character.name} className="w-full h-full object-cover" crossOrigin="use-credentials" />
            ) : (
              <UserIcon className="w-8 h-8 text-muted-foreground/40" />
            )}
          </div>
          {/* Name + meta */}
          <div className="flex-1 min-w-0">
            <EditableField
              value={character.name}
              onSave={(v) => updateCharacter({ name: v })}
              className="font-serif text-2xl text-foreground w-full"
              inputClassName="h-9 text-xl font-serif"
              data-testid="char-sheet-header-name"
            />
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="char-sheet-header-meta">
              {[character.race, character.character_class, `Level ${character.level || 1}`, type === "pc" ? "PC" : type]
                .filter(Boolean).join(" · ")}
            </p>
          </div>
          {/* Quick stats */}
          <div className="hidden md:flex items-center gap-4" data-testid="char-sheet-header-quickstats">
            <div className="text-center"><Heart className="w-3.5 h-3.5 text-red-400 mx-auto" /><p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">HP</p><p className="text-sm font-bold tabular-nums">{sheet.current_hp ?? "—"}/{sheet.max_hp ?? "—"}</p></div>
            <div className="text-center"><Shield className="w-3.5 h-3.5 text-blue-400 mx-auto" /><p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">AC</p><p className="text-sm font-bold tabular-nums">{sheet.armor_class ?? character.armor_class ?? "—"}</p></div>
            <div className="text-center"><p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Init</p><p className="text-sm font-bold tabular-nums">{formatMod(dexMod)}</p></div>
            <div className="text-center"><p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Prof</p><p className="text-sm font-bold tabular-nums">{formatMod(profB)}</p></div>
          </div>
          <span className="text-[10px] text-muted-foreground/70" data-testid="char-sheet-save-indicator">{dirty ? "Saving…" : "Saved"}</span>
        </div>

        {/* BODY — three theme variants */}
        {isCompanion ? (
          <div data-testid="char-sheet-companion-wrapper">
            <CompanionStatBlock entity={character} />
          </div>
        ) : (
          <ParchmentSheet
            character={character}
            sheet={sheet}
            updateSheet={updateSheet}
            updateCharacter={updateCharacter}
            mode={isNPC ? "npc" : "pc"}
          />
        )}
      </div>
    </Layout>
  );
};

export default CharacterSheetPage;
